import React, { useState, useEffect, useCallback } from 'react';
import { empApi } from '../lib/api';
import { toast } from 'sonner';
import {
  Clock, LogIn, Coffee, Undo2, LogOut, MapPin, MapPinOff, Smartphone, Delete,
  CheckCircle2, AlertTriangle, PartyPopper, Megaphone, Check, KeyRound, FileClock, ArrowLeft, Search, Building2, LogOut as Exit,
  Fingerprint, Umbrella,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ESTADO_LABEL, ESTADO_DOT, ACCION_CONFIG, formatHoras } from '../lib/labels';
import { soportaBiometria, prepararPeticion, serializarLogin } from '../lib/webauthn';
import BiometriaCard from '../components/empapp/BiometriaCard';
import InstalarAppCard from '../components/empapp/InstalarAppCard';
import AutoCierreAceptacionModal from '../components/empapp/AutoCierreAceptacionModal';
import AusenciasEmpApp from '../components/empapp/AusenciasEmpApp';

const ICONS = { LogIn, Coffee, Undo2, LogOut };
const TIPO_FESTIVO_LABEL = { nacional: 'Festivo nacional', autonomico: 'Festivo autonómico', local: 'Festivo local', empresa: 'Festivo de empresa' };

const obtenerUbicacion = () => new Promise((resolve) => {
  if (!navigator.geolocation) { resolve({ lat: null, lng: null, precision_m: null }); return; }
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, precision_m: Math.round(pos.coords.accuracy) }),
    () => resolve({ lat: null, lng: null, precision_m: null }),
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
  );
});

const Reloj = () => {
  const [ahora, setAhora] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setAhora(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div className="text-center">
      <div className="font-heading text-5xl font-bold tracking-tight tabular-nums text-white" data-testid="empapp-clock">
        {ahora.toLocaleTimeString('es-ES')}
      </div>
      <div className="text-white/70 text-sm capitalize mt-1">
        {ahora.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
      </div>
    </div>
  );
};

// ===== Login =====
const msgErr = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const Login = ({ onOk }) => {
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [bioUser, setBioUser] = useState(localStorage.getItem('empapp_bio_user') || '');
  const [bioDisponible, setBioDisponible] = useState(false);

  useEffect(() => { soportaBiometria().then(setBioDisponible); }, []);

  const entrarConHuella = async () => {
    setLoading(true);
    try {
      const opts = await empApi.post('/empapp/webauthn/login/options', { username: bioUser, origin: window.location.origin });
      const cred = await navigator.credentials.get({ publicKey: prepararPeticion(opts.data) });
      const res = await empApi.post('/empapp/webauthn/login/verify', { username: bioUser, credential: serializarLogin(cred), origin: window.location.origin });
      localStorage.setItem('empapp_token', res.data.access_token);
      onOk(res.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        localStorage.removeItem('empapp_bio_user');
        setBioUser('');
        toast.error(msgErr(err, 'No hay huella registrada. Usa tu PIN.'));
      } else if (err?.name === 'NotAllowedError') {
        toast.info('Huella cancelada. Puedes entrar con tu PIN.');
      } else {
        toast.error(msgErr(err, 'No se pudo verificar la huella. Usa tu PIN.'));
      }
    } finally { setLoading(false); }
  };

  const entrar = async (e) => {
    e.preventDefault();
    if (pin.length < 4) { toast.error('El PIN debe tener al menos 4 dígitos'); return; }
    setLoading(true);
    try {
      const res = await empApi.post('/empapp/login', { username: username.trim(), pin });
      localStorage.setItem('empapp_token', res.data.access_token);
      onOk(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo iniciar sesión');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="bg-indigo-500 p-3.5 rounded-2xl mb-4 shadow-lg shadow-indigo-500/30"><Smartphone className="h-8 w-8 text-white" /></div>
          <h1 className="font-heading text-2xl font-bold text-white">Fichaje móvil</h1>
          <p className="text-slate-400 text-sm mt-1">Accede con tu usuario y PIN</p>
        </div>
        {bioUser && bioDisponible && (
          <div className="mb-6">
            <Button onClick={entrarConHuella} disabled={loading} data-testid="empapp-bio-login-btn"
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-base font-semibold">
              <Fingerprint className="h-6 w-6 mr-2" /> Entrar con huella
            </Button>
            <div className="flex items-center gap-3 mt-5">
              <div className="flex-1 h-px bg-slate-800" />
              <span className="text-slate-500 text-xs">o con tu usuario y PIN</span>
              <div className="flex-1 h-px bg-slate-800" />
            </div>
          </div>
        )}
        <form onSubmit={entrar} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-slate-300">DNI</Label>
            <Input data-testid="empapp-username" value={username} autoCapitalize="none" autoComplete="username"
              onChange={(e) => setUsername(e.target.value)} required
              className="bg-slate-900 border-slate-700 text-white h-12" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300">PIN</Label>
            <Input data-testid="empapp-pin" type="password" inputMode="numeric" maxLength={6} value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} required
              className="bg-slate-900 border-slate-700 text-white h-12 text-center text-2xl tracking-[0.4em]" />
          </div>
          <Button type="submit" disabled={loading} data-testid="empapp-login-btn"
            className="w-full h-12 bg-indigo-500 hover:bg-indigo-600 text-base font-semibold">
            {loading ? 'Entrando...' : 'Entrar'}
          </Button>
        </form>
        <p className="text-slate-500 text-xs text-center mt-6">Si no puedes acceder, pide a tu administrador que active tu acceso a la app.</p>
        <p className="text-slate-600 text-xs text-center mt-3">Consejo: añade esta página a tu pantalla de inicio para usarla como una app.</p>
      </div>
    </div>
  );
};

// ===== Home =====
const EmployeeApp = () => {
  const [data, setData] = useState(null);
  const [logueado, setLogueado] = useState(!!localStorage.getItem('empapp_token'));
  const [cargando, setCargando] = useState(true);
  const [fichando, setFichando] = useState(false);
  const [pantalla, setPantalla] = useState('home');
  const [misFichajes, setMisFichajes] = useState(null);
  const [rango, setRango] = useState({ desde: '', hasta: '' });
  const [pinForm, setPinForm] = useState({ actual: '', nuevo: '', repetir: '' });
  const [cierresPendientes, setCierresPendientes] = useState([]);

  const color = data?.empleado?.color || '#4f46e5';

  const cargar = useCallback(async () => {
    if (!localStorage.getItem('empapp_token')) { setCargando(false); return; }
    try {
      const res = await empApi.get('/empapp/me');
      setData(res.data);
      setLogueado(true);
      setCierresPendientes(res.data.cierres_automaticos || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('empapp_token');
        setLogueado(false); setData(null);
        if (err.response?.status === 403) toast.error(err.response?.data?.detail || 'Acceso no disponible');
      }
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const onLoginOk = (d) => {
    setData(d); setLogueado(true); setCargando(false);
    setCierresPendientes(d.cierres_automaticos || []);
  };

  const responderAutoCierre = async (cierreId, aceptar, comentario) => {
    await empApi.post(`/empapp/auto-cierre/${cierreId}/responder`, { aceptar, comentario });
    toast.success(aceptar ? 'Cierre confirmado' : 'Rechazo enviado a tu empresa');
  };

  const salir = () => {
    localStorage.removeItem('empapp_token');
    setLogueado(false); setData(null); setPantalla('home');
    toast.success('Sesión cerrada');
  };

  const fichar = async (tipo) => {
    setFichando(true);
    const geoActivo = data?.centro?.geo_activo;
    try {
      let coords = { lat: null, lng: null, precision_m: null };
      if (geoActivo) {
        toast.info('Obteniendo tu ubicación...', { duration: 1500 });
        coords = await obtenerUbicacion();
      }
      const res = await empApi.post('/empapp/fichar', { tipo, ...coords });
      setData(res.data);
      if (res.data.incidencia_gps) {
        if (res.data.gps_motivo === 'fuera_de_zona') {
          toast.warning(`Fichaje registrado FUERA de la zona (${Math.round(res.data.distancia_m)} m). Se ha marcado como incidencia.`, { duration: 6000 });
        } else {
          toast.warning('Fichaje registrado SIN ubicación. Se ha marcado como incidencia.', { duration: 6000 });
        }
      } else {
        toast.success(`${ACCION_CONFIG[tipo].label} · ${res.data.hora}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo fichar');
      if (err.response?.status === 401 || err.response?.status === 403) salir();
    } finally { setFichando(false); }
  };

  const aceptarComunicado = async (id) => {
    try {
      await empApi.post('/empapp/comunicado/aceptar', { comunicado_id: id });
      toast.success('Comunicado aceptado');
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const cambiarPin = async () => {
    if (pinForm.nuevo.length < 4) { toast.error('El nuevo PIN debe tener al menos 4 dígitos'); return; }
    if (pinForm.nuevo !== pinForm.repetir) { toast.error('El nuevo PIN no coincide'); return; }
    try {
      await empApi.post('/empapp/cambiar-pin', { pin_actual: pinForm.actual, pin_nuevo: pinForm.nuevo });
      toast.success('PIN cambiado correctamente');
      setPinForm({ actual: '', nuevo: '', repetir: '' });
      setPantalla('home');
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo cambiar el PIN'); }
  };

  const buscarMisFichajes = async () => {
    try {
      const res = await empApi.post('/empapp/mis-fichajes', { desde: rango.desde || null, hasta: rango.hasta || null });
      setMisFichajes(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al consultar'); }
  };

  if (cargando) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400">Cargando...</div>;
  if (!logueado || !data) return <Login onOk={onLoginOk} />;

  const { empleado, centro, estado, acciones, eventos_hoy = [], festivo_hoy, comunicados_pendientes = [], ausencias_novedades = 0 } = data;

  // ===== Mis ausencias =====
  if (pantalla === 'mis_ausencias') {
    return (
      <AusenciasEmpApp
        onVolver={() => setPantalla('home')}
        onCambioNovedades={(n) => setData((d) => ({ ...d, ausencias_novedades: n }))}
      />
    );
  }

  // ===== Cambiar PIN =====
  if (pantalla === 'cambiar_pin') {
    return (
      <div className="min-h-screen bg-slate-950 p-6" data-testid="empapp-cambiar-pin-screen">
        <button onClick={() => setPantalla('home')} className="flex items-center gap-2 text-slate-400 mb-6"><ArrowLeft className="h-5 w-5" /> Volver</button>
        <div className="max-w-sm mx-auto space-y-4">
          <div className="text-center mb-2"><KeyRound className="h-9 w-9 mx-auto mb-2 text-indigo-400" /><h2 className="font-heading text-xl font-bold text-white">Cambiar mi PIN</h2></div>
          {[['actual', 'PIN actual'], ['nuevo', 'Nuevo PIN (4-6 dígitos)'], ['repetir', 'Repetir nuevo PIN']].map(([k, lbl]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-slate-300">{lbl}</Label>
              <Input type="password" inputMode="numeric" maxLength={6} value={pinForm[k]} data-testid={`empapp-pin-${k}`}
                onChange={(e) => setPinForm({ ...pinForm, [k]: e.target.value.replace(/\D/g, '') })}
                className="bg-slate-900 border-slate-700 text-white h-12 text-center text-xl tracking-widest" />
            </div>
          ))}
          <Button onClick={cambiarPin} data-testid="empapp-guardar-pin-btn" className="w-full h-12 bg-indigo-500 hover:bg-indigo-600">Guardar nuevo PIN</Button>
        </div>
      </div>
    );
  }

  // ===== Mis fichajes =====
  if (pantalla === 'mis_fichajes') {
    return (
      <div className="min-h-screen bg-slate-950 p-6" data-testid="empapp-mis-fichajes-screen">
        <button onClick={() => setPantalla('home')} className="flex items-center gap-2 text-slate-400 mb-6"><ArrowLeft className="h-5 w-5" /> Volver</button>
        <div className="max-w-md mx-auto">
          <div className="text-center mb-5"><FileClock className="h-9 w-9 mx-auto mb-2 text-indigo-400" /><h2 className="font-heading text-xl font-bold text-white">Mis fichajes</h2></div>
          <div className="flex items-end gap-2 mb-4">
            <div className="flex-1 space-y-1"><Label className="text-slate-300 text-xs">Desde</Label>
              <Input type="date" value={rango.desde} onChange={(e) => setRango({ ...rango, desde: e.target.value })} data-testid="empapp-desde" className="bg-slate-900 border-slate-700 text-white" /></div>
            <div className="flex-1 space-y-1"><Label className="text-slate-300 text-xs">Hasta</Label>
              <Input type="date" value={rango.hasta} onChange={(e) => setRango({ ...rango, hasta: e.target.value })} data-testid="empapp-hasta" className="bg-slate-900 border-slate-700 text-white" /></div>
            <Button onClick={buscarMisFichajes} data-testid="empapp-buscar-btn" className="bg-indigo-500 hover:bg-indigo-600 h-10"><Search className="h-4 w-4" /></Button>
          </div>
          {misFichajes && (
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-800 flex justify-between text-sm">
                <span className="font-semibold text-slate-200">{misFichajes.dias.length} jornada(s)</span>
                <span className="text-slate-400">Total: <b className="text-white">{formatHoras(misFichajes.total_horas)}</b></span>
              </div>
              {misFichajes.dias.map((d, i) => (
                <div key={i} className="flex justify-between items-center px-4 py-2.5 border-t border-slate-800 text-sm">
                  <span className="text-slate-300 tabular-nums">{new Date(d.fecha).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' })}</span>
                  <span className="text-slate-400 tabular-nums text-xs">
                    {d.entrada ? new Date(d.entrada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' }) : '--'}
                    {' → '}
                    {d.salida ? new Date(d.salida).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' }) : '--'}
                  </span>
                  <span className="font-semibold text-white tabular-nums">{formatHoras(d.horas_trabajadas)}</span>
                </div>
              ))}
              {misFichajes.dias.length === 0 && <div className="py-6 text-center text-slate-500 text-sm">Sin fichajes en ese periodo.</div>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== Home =====
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="empapp-home">
      {cierresPendientes.length > 0 && (
        <AutoCierreAceptacionModal
          cierres={cierresPendientes}
          onResponder={responderAutoCierre}
          onCerrar={() => { setCierresPendientes([]); cargar(); }}
        />
      )}
      <header className="flex items-center justify-between px-6 pt-6 pb-2">
        <div className="flex items-center gap-2 text-slate-300">
          <Building2 className="h-4 w-4" />
          <span className="text-sm font-medium truncate max-w-[180px]">{empleado.empresa_nombre || 'Mi empresa'}</span>
        </div>
        <button onClick={salir} data-testid="empapp-logout-btn" className="flex items-center gap-1.5 text-slate-400 hover:text-red-400 text-sm"><Exit className="h-4 w-4" /> Salir</button>
      </header>

      <div className="px-6 py-6" style={{ background: `linear-gradient(160deg, ${color}, #020617)` }}>
        <div className="mb-5"><Reloj /></div>
        <div className="text-center">
          <p className="text-white/80 text-sm">Hola,</p>
          <h1 className="font-heading text-2xl font-bold text-white">{empleado.full_name}</h1>
          <span className="inline-flex items-center gap-2 mt-2 px-3 py-1 rounded-full text-xs font-bold bg-white/15 text-white">
            <span className={`h-2 w-2 rounded-full ${ESTADO_DOT[estado]}`} /> {ESTADO_LABEL[estado]}
          </span>
        </div>
      </div>

      <div className="flex-1 px-6 py-6 space-y-4 max-w-md mx-auto w-full">
        {/* Estado GPS */}
        <div className="flex items-center justify-center gap-2 text-xs" data-testid="empapp-gps-estado">
          {centro?.geo_activo ? (
            <span className="flex items-center gap-1.5 text-emerald-400"><MapPin className="h-3.5 w-3.5" /> Ubicación requerida · {centro.nombre} ({centro.radio_m} m)</span>
          ) : (
            <span className="flex items-center gap-1.5 text-slate-500"><MapPinOff className="h-3.5 w-3.5" /> Sin geolocalización configurada</span>
          )}
        </div>

        {/* Aviso GPS ↑ · biometría e instalar app se muestran al final tras las acciones */}

        {festivo_hoy && (
          <div data-testid="empapp-festivo" className="flex items-center gap-3 rounded-xl border border-red-500/40 bg-red-500/10 text-red-300 px-4 py-3">
            <PartyPopper className="h-5 w-5 shrink-0" />
            <div><p className="font-bold text-sm leading-tight">Hoy es festivo</p>
              <p className="text-xs leading-tight">{festivo_hoy.nombre}{festivo_hoy.tipo ? ` · ${TIPO_FESTIVO_LABEL[festivo_hoy.tipo] || festivo_hoy.tipo}` : ''}</p></div>
          </div>
        )}

        {comunicados_pendientes.length > 0 && (
          <div className="space-y-2" data-testid="empapp-comunicados">
            {comunicados_pendientes.map((c) => (
              <div key={c.id} className="rounded-xl border border-blue-500/40 bg-blue-500/10 px-4 py-3" data-testid={`empapp-comunicado-${c.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Megaphone className="h-4 w-4 text-blue-300" />
                  {c.urgente && <span className="text-[10px] font-bold text-red-300 bg-red-500/20 px-1.5 py-0.5 rounded">URGENTE</span>}
                  <span className="font-semibold text-blue-100 text-sm">{c.titulo}</span>
                </div>
                <p className="text-xs text-blue-200/80 mb-2 whitespace-pre-wrap">{c.cuerpo}</p>
                <Button size="sm" onClick={() => aceptarComunicado(c.id)} data-testid={`empapp-aceptar-comunicado-${c.id}`}
                  className="bg-blue-600 hover:bg-blue-700 h-8 text-xs"><Check className="h-3.5 w-3.5 mr-1" /> He leído y acepto</Button>
              </div>
            ))}
          </div>
        )}

        {acciones.length === 0 && (
          <div className="text-center text-slate-400 bg-slate-900 border border-slate-800 rounded-2xl p-8" data-testid="empapp-no-actions">
            <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-red-400" />
            Tu jornada de hoy ya está finalizada. ¡Hasta mañana!
          </div>
        )}

        {acciones.map((accion) => {
          const cfg = ACCION_CONFIG[accion];
          const Icon = ICONS[cfg.icon];
          return (
            <button key={accion} onClick={() => fichar(accion)} disabled={fichando}
              data-testid={`empapp-fichar-${accion}`}
              className={`w-full h-20 text-xl font-bold rounded-2xl shadow-lg flex items-center justify-center gap-3 border-2 transition-all active:scale-95 disabled:opacity-50 ${cfg.btn}`}>
              <Icon className="h-7 w-7" strokeWidth={2.5} /> {cfg.label}
            </button>
          );
        })}

        {eventos_hoy.length > 0 && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Fichajes de hoy</p>
            <div className="space-y-1.5">
              {eventos_hoy.map((ev, i) => (
                <div key={i} className="flex justify-between items-center text-sm text-slate-300">
                  <span className="flex items-center gap-1.5">
                    {ACCION_CONFIG[ev.tipo]?.label}
                    {ev.incidencia_gps && <AlertTriangle className="h-3.5 w-3.5 text-amber-400" title="Fichaje con incidencia GPS" />}
                  </span>
                  <span className="tabular-nums text-slate-400">{new Date(ev.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' })}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <Button variant="outline" onClick={() => { setMisFichajes(null); setPantalla('mis_fichajes'); }} data-testid="empapp-mis-fichajes-btn"
            className="flex-1 bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800"><FileClock className="h-4 w-4 mr-1.5" /> Mis fichajes</Button>
          <Button variant="outline" onClick={() => setPantalla('mis_ausencias')} data-testid="empapp-mis-ausencias-btn"
            className="flex-1 bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800 relative">
            <Umbrella className="h-4 w-4 mr-1.5" /> Ausencias
            {ausencias_novedades > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-amber-500 text-white"
                data-testid="empapp-ausencias-badge"
              >{ausencias_novedades}</span>
            )}
          </Button>
          <Button variant="outline" onClick={() => { setPinForm({ actual: '', nuevo: '', repetir: '' }); setPantalla('cambiar_pin'); }} data-testid="empapp-cambiar-pin-btn"
            className="flex-1 bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800"><KeyRound className="h-4 w-4 mr-1.5" /> PIN</Button>
        </div>

        <div className="space-y-3 pt-2">
          <InstalarAppCard />
          <BiometriaCard username={empleado.username} />
        </div>
      </div>
    </div>
  );
};

export default EmployeeApp;
